import React from 'react';
import { Link } from 'react-router-dom';
import moment from 'moment-timezone';
import { FiClock } from 'react-icons/fi';

import { Text, textVariantsEnum, fontWeightEnum } from '@components/Text';
import { StatusBadge, statusBadgeTones } from '@components/StatusBadge';
import { InitialsBadge } from '@components/InitialsBadge';
import { appRoutes } from '@/routes/routes';

// Shared page-level styles (card, layout) live on the parent page's module
// so every sub-component reuses the same declarations instead of each
// redefining its own — see LateEarlyLoginAlerts.module.scss's `.card`.
import pageStyles from '../LateEarlyLoginAlerts.module.scss';
import styles from './SidePanels.module.scss';

const ALERT_STATUS_META = {
  alertSent: { label: 'Alert Sent', tone: statusBadgeTones.info },
  acknowledged: { label: 'Acknowledged', tone: statusBadgeTones.success },
};

const ALERT_TYPE_LABEL = {
  productivity: 'low productivity',
  lateLogin: 'late',
  earlyLogin: 'early',
};

export const RecentAlertsPanel = ({
  alerts,
  memberDirectory,
  timeZone,
  isLoading,
}) => (
  <div className={pageStyles.card}>
    <div className={styles.header}>
      <Text
        variant={textVariantsEnum.Text_sm}
        fontWeight={fontWeightEnum.Semibold}
        color="gray-900"
      >
        Recent Alerts
      </Text>
      <Link
        to={appRoutes().lateEarlyLoginAlerts}
        className={styles.viewAllLink}
      >
        View All
      </Link>
    </div>

    {isLoading ? (
      <Text variant={textVariantsEnum.Text_xs} color="gray-500">
        Loading…
      </Text>
    ) : alerts.length === 0 ? (
      <div className={pageStyles.emptyState}>No alerts yet today.</div>
    ) : (
      <div className={styles.alertList}>
        {alerts.map((alert) => {
          const member = memberDirectory[alert.userId] || {};
          const name = member.userName || member.userEmailid || alert.userId;
          const statusMeta =
            ALERT_STATUS_META[alert.status] || ALERT_STATUS_META.alertSent;
          const diffMinutes = alert.metadata?.checkInDiffMinutes;
          const detail =
            diffMinutes != null
              ? `${Math.abs(diffMinutes)}m ${ALERT_TYPE_LABEL[alert.type] || ''}`
              : ALERT_TYPE_LABEL[alert.type] || alert.type;
          const time = alert.createdAt?.toDate
            ? moment(alert.createdAt.toDate())
                .tz(timeZone || moment.tz.guess())
                .format('h:mm A')
            : '';

          return (
            <div key={alert.id} className={styles.alertItem}>
              <InitialsBadge label={name} twoLetter />
              <div className={styles.alertContent}>
                <Text
                  variant={textVariantsEnum.Text_xs}
                  fontWeight={fontWeightEnum.Semibold}
                  color="gray-900"
                >
                  {name}
                </Text>
                <Text variant={textVariantsEnum.Text_xs} color="gray-500">
                  {time} · {detail}
                </Text>
              </div>
              <StatusBadge label={statusMeta.label} tone={statusMeta.tone} />
            </div>
          );
        })}
      </div>
    )}
  </div>
);
RecentAlertsPanel.displayName = 'RecentAlertsPanel';

const POLICY_ROWS = [
  {
    key: 'scheduledStartTime',
    label: 'Scheduled start time',
    icon: <FiClock size={14} />,
  },
  {
    key: 'gracePeriodMinutes',
    label: 'Grace period',
    suffix: ' minutes',
    icon: <FiClock size={14} />,
  },
  {
    key: 'earlyLoginThresholdMinutes',
    label: 'Early login threshold',
    suffix: ' minutes early',
    icon: <FiClock size={14} />,
  },
  {
    key: 'criticalThresholdMinutes',
    label: 'Critical threshold',
    suffix: ' minutes late',
    icon: <FiClock size={14} />,
  },
];

export const AttendancePolicyPanel = ({
  alertConfig,
  workingSchedule,
  isConfigured,
  isLoading,
}) => {
  const values = {
    ...alertConfig,
    scheduledStartTime: workingSchedule?.startTime,
  };

  return (
    <div className={pageStyles.card}>
      <div className={styles.header}>
        <Text
          variant={textVariantsEnum.Text_sm}
          fontWeight={fontWeightEnum.Semibold}
          color="gray-900"
        >
          Attendance Policy
        </Text>
        <Link to={appRoutes().manageSettings} className={styles.viewAllLink}>
          Edit Policy
        </Link>
      </div>

      {isLoading ? (
        <Text variant={textVariantsEnum.Text_xs} color="gray-500">
          Loading…
        </Text>
      ) : !isConfigured ? (
        <div className={pageStyles.emptyState}>
          This team hasn&apos;t set up attendance alerts yet. Configure it in
          Settings → Alerts & Notifications.
        </div>
      ) : (
        <div className={styles.policyList}>
          {POLICY_ROWS.map((row) => (
            <div key={row.key} className={styles.policyRow}>
              <Text variant={textVariantsEnum.Text_xs} color="gray-600">
                {row.label}
              </Text>
              <Text
                variant={textVariantsEnum.Text_xs}
                fontWeight={fontWeightEnum.Semibold}
                color="gray-900"
              >
                {values[row.key] != null
                  ? `${values[row.key]}${row.suffix || ''}`
                  : '—'}
              </Text>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
AttendancePolicyPanel.displayName = 'AttendancePolicyPanel';
